import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Security as SecurityIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  NotificationsActive as NotificationsIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import axios from 'axios';
import config from '../../config';

const Security = ({ showMessage }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [ipLocks, setIpLocks] = useState([]);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [suspiciousActivities, setSuspiciousActivities] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState('');

  // LINE通知関連のstate
  const [lineNotificationStatus, setLineNotificationStatus] = useState({});
  const [testNotificationLoading, setTestNotificationLoading] = useState(false);

  // セキュリティAPI用のヘルパー関数（適切なトークンを使用）
  const makeSecurityApiRequest = async (method, endpoint, data = null) => {
    // 複数のトークンキーを試す
    const tokenKeys = ['authToken', 'access_token'];
    let token = null;
    let usedKey = null;
    
    for (const key of tokenKeys) {
      const storedToken = localStorage.getItem(key);
      if (storedToken) {
        token = storedToken;
        usedKey = key;
        break;
      }
    }

    console.log('🔐 [SECURITY API] トークン確認:', {
      endpoint,
      method,
      hasToken: !!token,
      tokenKey: usedKey,
      tokenStart: token ? token.substring(0, 20) + '...' : 'none',
      allKeys: tokenKeys.map(key => ({
        key,
        exists: !!localStorage.getItem(key),
        length: localStorage.getItem(key)?.length || 0
      }))
    });

    if (!token) {
      console.error('🔴 [SECURITY API] 認証トークンが見つかりません');
      throw new Error('認証トークンが見つかりません');
    }

    const config_req = {
      method,
      url: `${config.API_BASE_URL}${endpoint}`,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };

    if (data && (method.toLowerCase() === 'post' || method.toLowerCase() === 'put')) {
      config_req.data = data;
    }

    console.log('📤 [SECURITY API] リクエスト送信:', {
      url: config_req.url,
      method: config_req.method,
      hasAuth: !!config_req.headers.Authorization,
      authHeaderStart: config_req.headers.Authorization.substring(0, 30) + '...'
    });

    try {
      const response = await axios(config_req);
      console.log('✅ [SECURITY API] リクエスト成功:', {
        status: response.status,
        dataType: typeof response.data
      });
      return response;
    } catch (error) {
      console.error('❌ [SECURITY API] リクエストエラー:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        headers: error.response?.headers,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      
      // 500エラーの場合、サーバーから詳細なエラー情報を表示
      if (error.response?.status === 500 && error.response?.data) {
        console.error('🔥 [SECURITY API] サーバーエラー詳細:', error.response.data);
        
        // セキュリティダッシュボード用のエラーメッセージを設定
        if (error.response.data.error && error.response.data.suggestion) {
          console.warn('💡 [SECURITY API] 解決提案:', error.response.data.suggestion);
        }
      }
      
      throw error;
    }
  };

  // ページネーション状態
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // フィルター状態
  const [filters, setFilters] = useState({
    status: 'all',
    severity: '',
    eventType: '',
    ipAddress: ''
  });

  // 新しいIPロックフォーム
  const [newLockForm, setNewLockForm] = useState({
    ip_address: '',
    reason: '',
    duration_minutes: 60,
    is_permanent: false
  });
  const [showNewLockForm, setShowNewLockForm] = useState(false);

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'admin') {
      setError('管理者権限が必要です');
      setLoading(false);
      return;
    }
    
    loadSecurityData();
  }, [currentUser, activeTab, currentPage, filters]);

  const loadSecurityData = async () => {
    try {
      setLoading(true);
      setError('');

      switch (activeTab) {
        case 0:
          await loadOverview();
          break;
        case 1:
          await loadIpLocks();
          break;
        case 2:
          await loadSecurityLogs();
          break;
        case 3:
          await loadSuspiciousActivities();
          break;
        case 4:
          await loadLineNotificationStatus();
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('セキュリティデータの読み込みエラー:', error);
      
      // サーバーエラーの詳細情報をUIに表示
      let errorMessage = 'データの読み込み中にエラーが発生しました';
      if (error.response?.status === 500 && error.response?.data) {
        const serverError = error.response.data;
        if (serverError.error) {
          errorMessage = serverError.error;
          if (serverError.suggestion) {
            errorMessage += ` (${serverError.suggestion})`;
          }
        }
      }
      
      setError(errorMessage);
      showMessage && showMessage(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadOverview = async () => {
    const response = await makeSecurityApiRequest('GET', '/api/security/dashboard/overview');
    setOverview(response.data);
  };

  const loadIpLocks = async () => {
    const params = new URLSearchParams({
      page: currentPage,
      per_page: 20,
      status: filters.status
    });

    const response = await makeSecurityApiRequest('GET', `/api/security/ip-locks?${params}`);
    setIpLocks(response.data.locks);
    setTotalPages(response.data.pagination.pages);
  };

  const loadSecurityLogs = async () => {
    const params = new URLSearchParams({
      page: currentPage,
      per_page: 50
    });

    if (filters.severity) params.append('severity', filters.severity);
    if (filters.eventType) params.append('event_type', filters.eventType);
    if (filters.ipAddress) params.append('ip_address', filters.ipAddress);

    const response = await makeSecurityApiRequest('GET', `/api/security/security-logs?${params}`);
    setSecurityLogs(response.data.logs);
    setTotalPages(response.data.pagination.pages);
  };

  const loadSuspiciousActivities = async () => {
    const params = new URLSearchParams({
      page: currentPage,
      per_page: 20,
      status: filters.status || 'detected'
    });

    const response = await makeSecurityApiRequest('GET', `/api/security/suspicious-activities?${params}`);
    setSuspiciousActivities(response.data.activities);
    setTotalPages(response.data.pagination.pages);
  };

  const loadLineNotificationStatus = async () => {
    try {
      const response = await makeSecurityApiRequest('GET', '/api/line-notifications/status');
      setLineNotificationStatus(response.data);
    } catch (error) {
      console.error('LINE通知設定の読み込みエラー:', error);
      setError('LINE通知設定を読み込めませんでした');
    }
  };

  const handleTestLineNotification = async () => {
    try {
      setTestNotificationLoading(true);
      console.log('🧪 [LINE-TEST] Starting LINE notification test...');
      
      const response = await makeSecurityApiRequest('POST', '/api/line-notifications/test', {
        type: 'test',
        message: `🔔 管理画面からのテスト通知 - ${new Date().toLocaleString('ja-JP')}`
      });
      
      console.log('🧪 [LINE-TEST] Response received:', response);
      console.log('🧪 [LINE-TEST] Response data:', response.data);
      
      if (response.data.success) {
        console.log('✅ [LINE-TEST] Test notification sent successfully');
        showMessage && showMessage('テスト通知を送信しました', 'success');
      } else {
        console.error('❌ [LINE-TEST] Test notification failed');
        console.error('❌ [LINE-TEST] Failure message:', response.data.message);
        console.error('❌ [LINE-TEST] Debug info:', response.data.debug_info);
        console.error('❌ [LINE-TEST] Status code:', response.data.status_code);
        console.error('❌ [LINE-TEST] Response text:', response.data.response_text);
        
        let errorMessage = 'テスト通知の送信に失敗しました';
        if (response.data.message) {
          errorMessage += ': ' + response.data.message;
        }
        if (response.data.status_code) {
          errorMessage += ' (HTTP ' + response.data.status_code + ')';
        }
        
        showMessage && showMessage(errorMessage, 'error');
      }
    } catch (error) {
      console.error('❌ [LINE-TEST] Exception during test notification:', error);
      console.error('❌ [LINE-TEST] Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      
      let errorMessage = 'テスト通知の送信でエラーが発生しました';
      if (error.response?.status) {
        errorMessage += ' (HTTP ' + error.response.status + ')';
      }
      if (error.response?.data?.message) {
        errorMessage += ': ' + error.response.data.message;
      }
      
      showMessage && showMessage(errorMessage, 'error');
    } finally {
      setTestNotificationLoading(false);
    }
  };

  const handleUnlockIp = async (lockId) => {
    if (!window.confirm('このIPロックを解除しますか？')) return;

    try {
      await makeSecurityApiRequest('POST', `/api/security/ip-locks/${lockId}/unlock`);
      showMessage && showMessage('IPロックを解除しました', 'success');
      loadIpLocks();
    } catch (error) {
      console.error('IPロック解除エラー:', error);
      showMessage && showMessage('IPロック解除中にエラーが発生しました', 'error');
    }
  };

  const handleCreateIpLock = async (e) => {
    e.preventDefault();
    
    try {
      await makeSecurityApiRequest('POST', '/api/security/ip-locks', newLockForm);
      showMessage && showMessage('IPロックを作成しました', 'success');
      setShowNewLockForm(false);
      setNewLockForm({
        ip_address: '',
        reason: '',
        duration_minutes: 60,
        is_permanent: false
      });
      loadIpLocks();
    } catch (error) {
      console.error('IPロック作成エラー:', error);
      showMessage && showMessage('IPロック作成中にエラーが発生しました', 'error');
    }
  };

  const handleResolveSuspiciousActivity = async (activityId, status, notes = '') => {
    try {
      await makeSecurityApiRequest('POST', `/api/security/suspicious-activities/${activityId}/resolve`, {
        status,
        notes
      });
      showMessage && showMessage('疑わしい活動を更新しました', 'success');
      loadSuspiciousActivities();
    } catch (error) {
      console.error('疑わしい活動更新エラー:', error);
      showMessage && showMessage('更新中にエラーが発生しました', 'error');
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('ja-JP');
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  const getRiskScoreColor = (score) => {
    if (score >= 80) return 'error';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'info';
    return 'success';
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setCurrentPage(1);
  };

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          管理者権限が必要です
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SecurityIcon sx={{ mr: 1, fontSize: 32 }} />
        <Typography variant="h4">
          セキュリティダッシュボード
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title="データを更新">
            <IconButton onClick={loadSecurityData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="概要" />
          <Tab label="IPロック" />
          <Tab label="セキュリティログ" />
          <Tab label="疑わしい活動" />
          <Tab label="📱 LINE通知設定" />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {/* 概要タブ */}
          {activeTab === 0 && overview && (
            <Box>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        アクティブIPロック
                      </Typography>
                      <Typography variant="h3">
                        {overview.overview.active_ip_locks}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        過去24時間のイベント
                      </Typography>
                      <Typography variant="h3">
                        {overview.overview.recent_events_24h}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="error">
                        高リスクイベント
                      </Typography>
                      <Typography variant="h3" color="error">
                        {overview.overview.high_risk_events_24h}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="warning.main">
                        未解決の疑わしい活動
                      </Typography>
                      <Typography variant="h3" color="warning.main">
                        {overview.overview.suspicious_activities}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        レート制限違反（7日）
                      </Typography>
                      <Typography variant="h3">
                        {overview.overview.rate_limit_violations_7d}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  最新のセキュリティイベント
                </Typography>
                {overview.latest_events.map(event => (
                  <Card key={event.id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Chip 
                          label={event.severity}
                          color={getSeverityColor(event.severity)}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          {event.event_type}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                          {formatDateTime(event.created_at)}
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        {event.description}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        IP: {event.ip_address}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Paper>
            </Box>
          )}

          {/* IPロックタブ */}
          {activeTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>ステータス</InputLabel>
                  <Select
                    value={filters.status}
                    label="ステータス"
                    onChange={(e) => {
                      setFilters({...filters, status: e.target.value});
                      setCurrentPage(1);
                    }}
                  >
                    <MenuItem value="all">すべて</MenuItem>
                    <MenuItem value="active">アクティブ</MenuItem>
                    <MenuItem value="expired">期限切れ</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowNewLockForm(true)}
                >
                  新しいIPロック
                </Button>
              </Box>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>IPアドレス</TableCell>
                      <TableCell>理由</TableCell>
                      <TableCell>作成日時</TableCell>
                      <TableCell>期限</TableCell>
                      <TableCell>ステータス</TableCell>
                      <TableCell>アクション</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ipLocks.map((lock) => (
                      <TableRow key={lock.id}>
                        <TableCell>{lock.ip_address}</TableCell>
                        <TableCell>{lock.reason}</TableCell>
                        <TableCell>{formatDateTime(lock.created_at)}</TableCell>
                        <TableCell>
                          {lock.is_permanent ? '永続' : formatDateTime(lock.expires_at)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={lock.is_active ? 'アクティブ' : '期限切れ'}
                            color={lock.is_active ? 'error' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {lock.is_active && (
                            <Button
                              size="small"
                              startIcon={<LockOpenIcon />}
                              onClick={() => handleUnlockIp(lock.id)}
                            >
                              解除
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(event, value) => setCurrentPage(value)}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* セキュリティログタブ */}
          {activeTab === 2 && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>重要度</InputLabel>
                    <Select
                      value={filters.severity}
                      label="重要度"
                      onChange={(e) => {
                        setFilters({...filters, severity: e.target.value});
                        setCurrentPage(1);
                      }}
                    >
                      <MenuItem value="">すべて</MenuItem>
                      <MenuItem value="critical">Critical</MenuItem>
                      <MenuItem value="error">Error</MenuItem>
                      <MenuItem value="warning">Warning</MenuItem>
                      <MenuItem value="info">Info</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="イベントタイプ"
                    value={filters.eventType}
                    onChange={(e) => {
                      setFilters({...filters, eventType: e.target.value});
                      setCurrentPage(1);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="IPアドレス"
                    value={filters.ipAddress}
                    onChange={(e) => {
                      setFilters({...filters, ipAddress: e.target.value});
                      setCurrentPage(1);
                    }}
                  />
                </Grid>
              </Grid>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>日時</TableCell>
                      <TableCell>重要度</TableCell>
                      <TableCell>イベントタイプ</TableCell>
                      <TableCell>IPアドレス</TableCell>
                      <TableCell>説明</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {securityLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{formatDateTime(log.created_at)}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.severity}
                            color={getSeverityColor(log.severity)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{log.event_type}</TableCell>
                        <TableCell>{log.ip_address}</TableCell>
                        <TableCell>{log.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(event, value) => setCurrentPage(value)}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* 疑わしい活動タブ */}
          {activeTab === 3 && (
            <Box>
              <FormControl size="small" sx={{ mb: 3, minWidth: 120 }}>
                <InputLabel>ステータス</InputLabel>
                <Select
                  value={filters.status}
                  label="ステータス"
                  onChange={(e) => {
                    setFilters({...filters, status: e.target.value});
                    setCurrentPage(1);
                  }}
                >
                  <MenuItem value="detected">検出済み</MenuItem>
                  <MenuItem value="investigating">調査中</MenuItem>
                  <MenuItem value="resolved">解決済み</MenuItem>
                  <MenuItem value="false_positive">誤検出</MenuItem>
                </Select>
              </FormControl>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>検出日時</TableCell>
                      <TableCell>IPアドレス</TableCell>
                      <TableCell>リスクスコア</TableCell>
                      <TableCell>説明</TableCell>
                      <TableCell>ステータス</TableCell>
                      <TableCell>アクション</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suspiciousActivities.map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell>{formatDateTime(activity.detected_at)}</TableCell>
                        <TableCell>{activity.ip_address}</TableCell>
                        <TableCell>
                          <Chip
                            label={activity.risk_score}
                            color={getRiskScoreColor(activity.risk_score)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{activity.description}</TableCell>
                        <TableCell>
                          <Chip
                            label={activity.status}
                            color={activity.status === 'resolved' ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {activity.status === 'detected' && (
                            <Box>
                              <Button
                                size="small"
                                onClick={() => handleResolveSuspiciousActivity(activity.id, 'investigating')}
                                sx={{ mr: 1 }}
                              >
                                調査中
                              </Button>
                              <Button
                                size="small"
                                onClick={() => handleResolveSuspiciousActivity(activity.id, 'resolved')}
                                sx={{ mr: 1 }}
                              >
                                解決
                              </Button>
                              <Button
                                size="small"
                                onClick={() => handleResolveSuspiciousActivity(activity.id, 'false_positive')}
                              >
                                誤検出
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(event, value) => setCurrentPage(value)}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* LINE通知設定タブ */}
          {activeTab === 4 && (
            <Box>
              <Grid container spacing={3}>
                {/* 設定状況カード */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader 
                      title="📱 LINE通知設定状況"
                      avatar={<NotificationsIcon />}
                    />
                    <CardContent>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Channel Access Token:
                        </Typography>
                        <Typography variant="body1">
                          {lineNotificationStatus.has_channel_token ? 
                            '✅ 設定済み' : '❌ 未設定'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Admin User ID:
                        </Typography>
                        <Typography variant="body1">
                          {lineNotificationStatus.has_admin_user_id ? 
                            '✅ 設定済み' : '❌ 未設定'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          通知機能:
                        </Typography>
                        <Typography variant="body1">
                          {lineNotificationStatus.is_configured ? 
                            '🟢 有効' : '🔴 無効'}
                        </Typography>
                      </Box>

                      {lineNotificationStatus.is_configured && (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<NotificationsIcon />}
                          onClick={handleTestLineNotification}
                          disabled={testNotificationLoading}
                          fullWidth
                          sx={{ mt: 2 }}
                        >
                          {testNotificationLoading ? 
                            <CircularProgress size={20} /> : 
                            'テスト通知を送信'}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* 設定ガイドカード */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader 
                      title="🛠️ 設定ガイド"
                    />
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        かんたん設定（Messaging API・ブロードキャスト方式）
                      </Typography>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        LINE Notify は2025年3月に終了しました。現在は LINE Messaging API を使用します。
                        下記の方式なら Webhook や User ID の取得は不要で、Channel Access Token を1つ登録するだけで設定できます。
                      </Alert>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" component="div">
                          <strong>1. LINE公式アカウント（Messaging API）を作成</strong><br/>
                          • <a href="https://developers.line.biz/console/" target="_blank" rel="noopener noreferrer">LINE Developers Console</a> でプロバイダー／Messaging API チャネルを作成<br/>
                          • チャネル基本設定で「あいさつメッセージ」「応答メッセージ」はオフ推奨
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" component="div">
                          <strong>2. 通知を受け取る人が友だち追加</strong><br/>
                          • チャネルのQRコードから、通知を受けたい管理者が公式アカウントを友だち追加<br/>
                          • ブロードキャスト送信のため User ID の取得は不要
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" component="div">
                          <strong>3. Channel Access Token を取得</strong><br/>
                          • 「Messaging API設定」→「チャネルアクセストークン（長期）」を発行してコピー
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" component="div">
                          <strong>4. 環境変数に登録（再デプロイ不要の反映）</strong><br/>
                          • Cloud Run の環境変数 <code>LINE_CHANNEL_ACCESS_TOKEN</code> にトークンを設定<br/>
                          • 設定後、この画面の「テスト通知を送信」で動作確認
                        </Typography>
                      </Box>
                      <Alert severity="warning">
                        Messaging API は月200通を超えると有料プランが必要です（セキュリティ通知用途では通常無料枠で十分）。
                      </Alert>
                    </CardContent>
                  </Card>
                </Grid>

                {/* 通知イベント設定カード */}
                <Grid item xs={12}>
                  <Card>
                    <CardHeader 
                      title="🚨 通知対象イベント"
                    />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ p: 2, border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                            <Typography variant="h6" color="error">
                              🔒 ログイン失敗
                            </Typography>
                            <Typography variant="body2">
                              連続5回失敗でIP自動ロック時に通知
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ p: 2, border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                            <Typography variant="h6" color="warning.main">
                              ⚠️ レート制限
                            </Typography>
                            <Typography variant="body2">
                              API制限超過時に通知
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ p: 2, border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                            <Typography variant="h6" color="error">
                              🔍 異常検出
                            </Typography>
                            <Typography variant="body2">
                              脅威スコア80以上で通知
                            </Typography>
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ p: 2, border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                            <Typography variant="h6" color="info.main">
                              🔐 IPロック
                            </Typography>
                            <Typography variant="body2">
                              管理者によるIPロック実行時に通知
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          💡 <strong>設定例（Cloud Run 環境変数）:</strong><br/>
                          <code style={{ background: '#f5f5f5', padding: '8px', display: 'block', marginTop: '8px' }}>
                            LINE_CHANNEL_ACCESS_TOKEN = "（長期チャネルアクセストークン）"
                          </code>
                          <span style={{ display: 'block', marginTop: '8px' }}>
                            ※ 特定の1人だけに送りたい場合のみ <code>LINE_ADMIN_USER_ID</code> を追加設定すると個別送信（push）になります。未設定なら友だち全員へのブロードキャスト送信です。
                          </span>
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      )}

      {/* 新しいIPロック作成ダイアログ */}
      <Dialog open={showNewLockForm} onClose={() => setShowNewLockForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>新しいIPロックを作成</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="IPアドレス"
            value={newLockForm.ip_address}
            onChange={(e) => setNewLockForm({...newLockForm, ip_address: e.target.value})}
            placeholder="例: 192.168.1.1"
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="理由"
            value={newLockForm.reason}
            onChange={(e) => setNewLockForm({...newLockForm, reason: e.target.value})}
            placeholder="ロック理由を入力"
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="期間（分）"
            type="number"
            value={newLockForm.duration_minutes}
            onChange={(e) => setNewLockForm({...newLockForm, duration_minutes: parseInt(e.target.value)})}
            disabled={newLockForm.is_permanent}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newLockForm.is_permanent}
                onChange={(e) => setNewLockForm({...newLockForm, is_permanent: e.target.checked})}
              />
            }
            label="永続的なロック"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewLockForm(false)}>
            キャンセル
          </Button>
          <Button onClick={handleCreateIpLock} variant="contained">
            作成
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Security;